import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { requestWhatsappOtp, verifyWhatsappOtp } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useIsMobile, MobileOnlyNotice } from '../components/MobileOnly';

/**
 * Login B2C via OTP WhatsApp (Marina 2026-07-11).
 * Numero → codice OTP su WhatsApp → login come prospect → mappa B2C.
 * Rotta pubblica /b2c. Se già loggato → dritto in mappa.
 */
export default function B2CLogin() {
  const { user, loading: authLoading, logout } = useAuth();
  const isMobile = useIsMobile();
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function sendOtp(e) {
    e?.preventDefault();
    setError(''); setLoading(true);
    try {
      await requestWhatsappOtp(phone);
      setStep('otp');
    } catch (err) {
      setError(err?.response?.data?.error || 'Errore nell’invio del codice');
    } finally { setLoading(false); }
  }

  async function verify(e) {
    e?.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await verifyWhatsappOtp(phone, code);
      window.location.href = data?.redirect || '/app/home';
    } catch (err) {
      if (err?.response?.data?.code === 'expired') { setStep('phone'); setCode(''); }
      setError(err?.response?.data?.error || 'Codice non valido');
      setLoading(false);
    }
  }

  // Se sei loggato con un ALTRO ruolo (B2B, non via numero WhatsApp) e apri il link
  // B2C → logout automatico, così atterri sulla schermata del numero. Marina 2026-07-14.
  useEffect(() => {
    if (!authLoading && user && !user.is_prospect) {
      logout().catch(() => {});
    }
  }, [authLoading, user, logout]);

  // La parte B2C è disponibile SOLO da mobile.
  if (!isMobile) return <MobileOnlyNotice />;

  if (authLoading) return null;
  // Prospect già loggato (login col numero) → dritto in Home.
  if (user && user.is_prospect) return <Navigate to="/app/home" replace />;
  // Utente di altro ruolo: stiamo effettuando il logout automatico (effect sopra) →
  // niente redirect verso l'area B2B, mostriamo (a breve) il form del numero.
  if (user && !user.is_prospect) return null;

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.brand}>Rivelasta</div>
        {new URLSearchParams(window.location.search).get('superseded') === '1' && (
          <div style={{
            background: '#FFF4E5', border: '1px solid #F5C77E', color: '#8A5A00',
            borderRadius: 10, padding: '10px 12px', fontSize: 13, lineHeight: 1.4, marginBottom: 16,
          }}>
            Sei stato disconnesso: è stato effettuato l'accesso con questo numero da un altro dispositivo.
          </div>
        )}
        {step === 'phone' ? (
          <form onSubmit={sendOtp}>
            <h1 style={S.h1}>Entra con WhatsApp</h1>
            <p style={S.sub}>Inserisci il tuo numero: ti inviamo un codice su WhatsApp per accedere.</p>
            <input
              style={S.input}
              type="tel"
              inputMode="tel"
              autoFocus
              placeholder="Es. 333 1234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {error && <div style={S.err}>{error}</div>}
            <button style={S.btn} type="submit" disabled={loading || phone.replace(/\D/g, '').length < 8}>
              {loading ? 'Invio…' : 'Ricevi il codice su WhatsApp'}
            </button>
          </form>
        ) : (
          <form onSubmit={verify}>
            <h1 style={S.h1}>Inserisci il codice</h1>
            <p style={S.sub}>Ti abbiamo inviato un codice su WhatsApp al numero <b>{phone}</b>.</p>
            <input
              style={{ ...S.input, letterSpacing: '0.4em', textAlign: 'center', fontSize: 22 }}
              type="text"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              placeholder="______"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            {error && <div style={S.err}>{error}</div>}
            <button style={S.btn} type="submit" disabled={loading || code.length < 4}>
              {loading ? 'Verifico…' : 'Accedi'}
            </button>
            <button
              style={S.link}
              type="button"
              onClick={() => { setStep('phone'); setCode(''); setError(''); }}
            >
              ← Cambia numero
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)', padding: 20,
    fontFamily: 'Manrope, system-ui, sans-serif',
  },
  card: {
    width: '100%', maxWidth: 380, background: '#fff', borderRadius: 20, padding: '32px 26px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
  },
  brand: { fontWeight: 800, fontSize: 18, color: '#16915E', marginBottom: 18, letterSpacing: '-0.01em' },
  h1: { fontSize: 22, fontWeight: 800, margin: '0 0 6px', color: '#0f172a' },
  sub: { fontSize: 14, color: '#64748b', margin: '0 0 18px', lineHeight: 1.5 },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '13px 14px', fontSize: 16,
    border: '1.5px solid #e2e8f0', borderRadius: 12, outline: 'none', marginBottom: 12,
  },
  btn: {
    width: '100%', padding: '13px 16px', fontSize: 15, fontWeight: 700, color: '#fff',
    background: '#16915E', border: 'none', borderRadius: 12, cursor: 'pointer',
  },
  link: {
    width: '100%', marginTop: 12, padding: 8, background: 'none', border: 'none',
    color: '#64748b', fontSize: 13, cursor: 'pointer',
  },
  err: { color: '#dc2626', fontSize: 13, margin: '2px 0 12px' },
};
